// Hook registry — assembles all hooks into the Hooks object for LiveSocket.
// Small hooks (< 65 LOC) that share no state live here inline.

import {
  DiffInlineWrap,
  DiffCommentBtn,
  attachDiffInteractions,
  detachDiffInteractions,
} from "../diff-inline-overlay";
import EasyMDE from "easymde";
// easymde.min.css is @import'd from assets/css/app.css — do not side-effect
// import it here; vite would bake it into app.js and leak onto every page.
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type {
  LiveViewHook,
  MarkdownEditorState,
  RecentCommitsState,
  ToastState,
  PrTimelineState,
  ClipboardState,
  SshKeyPasteState,
  CodeRefAutocompleteState,
  EditorPushEventWire,
  PrPushEventWire,
  SearchPushEventWire,
} from "./types";
import type {
  AuthHook,
  CiHook,
  EditorHook,
  OmnisearchHook,
  PrHook,
  ProfileHook,
  SshHook,
  ThemeHook,
  UiHook,
} from "../../../.dox/ui";
import { illuminateCssClassModal } from "../../../.dox/ui";
import { isEditableTarget } from "./types";

const FADE_OUT = illuminateCssClassModal("fadeOut");
import { HistoryViewerHook } from "../history-viewer";

// Domain modules
import {
  CodeEditor,
  CommentAnchor,
  PaneDivider,
  ReadmeHighlight,
  VimToggle,
  EditorModeButton,
  KeyboardShortcuts,
} from "./code-browser";
import { OmniSearchTrigger, OmniSearch } from "./search";
import { BranchSelect } from "./branch_select";
import { AuthError, AuthCard, DotMatrixBg } from "./auth";
import {
  ProfileIdentity,
  ProfileAbout,
  ProjectStories,
  ActivityViz,
  Engagement,
  ContribTerrain,
} from "./profile";
import { Modal } from "./modal";
import { Dropdown } from "./dropdown";
import { InlineCommentForm } from "./inline-comment-form";
import { PipelineFavicon } from "./pipeline-favicon";
import { TableSortAnimate } from "./table-sort-animate";
import { ThemeApply } from "./theme-apply";
import { RowExpander } from "./row_expander";
import { CommitPanelToggle } from "./commit_panel_toggle";
import { WorkspaceTerminal } from "./workspace-terminal";
import { FileExpander } from "./file_expander";
import { DeleteConfirmGate } from "./delete_confirm_gate";
import { WorkspacesNavToggle } from "./workspaces_nav_toggle";
import { ClientTableSort } from "./client_table_sort";
import { ClientListFilter } from "./client_list_filter";
import { DisclosureToggle } from "./disclosure_toggle";
import { SectionToggle } from "./section_toggle";
import {
  DagGraph,
  BuildLog,
  EvalLog,
  Sparkline,
  CiAnimations,
  KdeChart,
  ScatterPlot,
  JobDetailPanel,
  JobMetricsChart,
  RunnerMetricsChart,
  SessionMetricsChart,
} from "./ci-pipeline";
import { formatDurationMs } from "./ci-pipeline/helpers";
// Dox-owned id (PrElementId in ui/pr.dox); rename upstream surfaces
// as a tsc error rather than a silent `null` from `getElementById`.
// validPrElementId is the wire-form (JSON) validator — illuminate* is
// for human-readable labels, not the rendered DOM id we have to match.
import { validPrElementId } from "../../../.dox/ui";
import { makeWorkerPool } from "./diff-worker-pool";
import {
  lcsRanges,
  pairAdjacentBlocks,
  type CharDiffResult,
  type CharDiffPair,
} from "./char-diff";
import {
  matchAutocompleteTrigger,
  spliceAutocomplete,
} from "./text-autocomplete";

gsap.registerPlugin(ScrollTrigger);
window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;

// ProfileDropdown — pure client-side profile nav dropdown (ts-7).
// Inlined because profile_dropdown.ts is not yet git-tracked; once tracked,
// replace this with: import { ProfileDropdown } from "./profile_dropdown"
// Replaces the server-side profile_toggle / profile_close handlers in nav_hook.ex.
const ProfileDropdown = {
  mounted(
    this: LiveViewHook<never> & { _onDocClick: (e: MouseEvent) => void },
  ) {
    const toggle = (): void => {
      const panel = this.el.querySelector<HTMLElement>("[data-profile-menu]");
      const isOpen = panel?.hasAttribute("data-open");
      document
        .querySelectorAll<HTMLElement>("[data-nav-panel]")
        .forEach((p) => {
          p.removeAttribute("data-open");
        });
      if (!isOpen && panel) panel.setAttribute("data-open", "");
    };
    this.el
      .querySelector<HTMLElement>("[data-profile-trigger]")
      ?.addEventListener("click", toggle);
    this._onDocClick = (e: MouseEvent): void => {
      if (!this.el.contains(e.target as Node)) {
        this.el
          .querySelector<HTMLElement>("[data-profile-menu]")
          ?.removeAttribute("data-open");
      }
    };
    document.addEventListener("click", this._onDocClick);
  },
  destroyed(
    this: LiveViewHook<never> & { _onDocClick: (e: MouseEvent) => void },
  ) {
    document.removeEventListener("click", this._onDocClick);
  },
};

// Re-export pure helpers that used to live here so existing imports keep working
// (tests import { lcsRanges, pairAdjacentBlocks, matchAutocompleteTrigger,
// spliceAutocomplete } from "../../ts/hooks").
export {
  lcsRanges,
  pairAdjacentBlocks,
  matchAutocompleteTrigger,
  spliceAutocomplete,
};
export type { CharDiffResult, CharDiffPair };

// --- DiffHighlight: parallel syntax highlighting via WebWorker pool ---
// Viewport-first: files visible to the user are highlighted before off-screen files.
// Progressive: each file's result is painted the instant the worker responds.
// Transferable: worker sends ArrayBuffer (zero-copy) instead of cloned string arrays.

const decoder = new TextDecoder();

const decodeLines = (buf: ArrayBuffer): string[] =>
  decoder.decode(buf).split("\0");

// Singleton worker pool — all state lives in the closure inside this record.
const DIFF_WORKER_COUNT = Math.min(navigator.hardwareConcurrency || 4, 8);
const diffPool = makeWorkerPool(DIFF_WORKER_COUNT);

// ── Viewport-priority queue ──
// Files that are visible get dispatched immediately.
// Off-screen files wait until they scroll into view.

interface PendingFile {
  readonly el: HTMLElement;
  readonly cells: HTMLElement[];
  readonly code: string;
  readonly lang: string;
}

// All files go through IntersectionObserver — no synchronous getBoundingClientRect
// that would force layout reflow and jump scroll during LiveView patching.
// Visible files fire immediately (IO callback is async, after layout settles).
const pendingFiles = new Map<HTMLElement, PendingFile>();

const applyHighlight = (cells: HTMLElement[], buf: ArrayBuffer): void => {
  const lines = decodeLines(buf);
  for (let i = 0; i < cells.length; i++) {
    const line = lines[i];
    if (line !== undefined) cells[i]!.innerHTML = line;
    cells[i]!.dataset["highlighted"] = "1";
  }
};

// ── Intra-line (character-level) diff ──
// After a file's lines are syntax-highlighted, walk paired del/add blocks and
// wrap the differing character ranges in extra spans so reviewers see exactly
// which chars changed inside a paired line. Pure server-side data, pure DOM
// markup — no extra round-trips and `textContent` is unchanged, so the row
// count the server-side `predict_file_height/2` uses for `contain-intrinsic-size`
// is still accurate after this pass.

const CHAR_DIFF_MAX_LINE = 500; // skip very long lines; LCS is O(n*m)
const CHAR_DIFF_MIN_SIM = 0.3; // skip pairs that share less than 30% chars

// Wrap each [start, end) char range inside the cell with a <span class={cls}>.
// Ranges are over the cell's joined textContent (so they survive whatever
// span nesting TreeSitter injected). Ops within a single text node are applied
// right-to-left so earlier offsets stay valid after splits.
export const wrapCharRanges = (
  cell: HTMLElement,
  ranges: ReadonlyArray<readonly [number, number]>,
  cls: string,
): void => {
  if (ranges.length === 0) return;
  const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
  interface Seg {
    readonly node: Text;
    readonly start: number;
    readonly end: number;
  }
  const segments: Seg[] = [];
  let pos = 0;
  let n: Node | null;
  while ((n = walker.nextNode())) {
    const t = n as Text;
    segments.push({ node: t, start: pos, end: pos + t.length });
    pos += t.length;
  }
  interface Op {
    readonly localStart: number;
    readonly localEnd: number;
  }
  const byNode = new Map<Text, Op[]>();
  for (const [rs, re] of ranges) {
    for (const seg of segments) {
      if (seg.end <= rs) continue;
      if (seg.start >= re) break;
      const localStart = Math.max(0, rs - seg.start);
      const localEnd = Math.min(seg.end - seg.start, re - seg.start);
      if (localStart >= localEnd) continue;
      const arr = byNode.get(seg.node) ?? [];
      arr.push({ localStart, localEnd });
      byNode.set(seg.node, arr);
    }
  }
  for (const [node, ops] of byNode) {
    if (!node.parentNode) continue;
    ops.sort((p, q) => q.localStart - p.localStart);
    for (const { localStart, localEnd } of ops) {
      const range = document.createRange();
      range.setStart(node, localStart);
      range.setEnd(node, localEnd);
      const span = document.createElement("span");
      span.className = cls;
      try {
        range.surroundContents(span);
      } catch {
        // Range crosses a non-text boundary — shouldn't happen since each op
        // is confined to a single text node. Swallow rather than throw.
      }
    }
  }
};

export const applyCharDiffToFile = (root: HTMLElement): void => {
  const rows = Array.from(root.querySelectorAll<HTMLTableRowElement>("tr"));
  for (const { delCell, addCell } of pairAdjacentBlocks(rows)) {
    if (
      delCell.querySelector(".diff-char-del") ||
      addCell.querySelector(".diff-char-add")
    )
      continue;
    const a = delCell.textContent;
    const b = addCell.textContent;
    if (a === b || a.length === 0 || b.length === 0) continue;
    if (a.length > CHAR_DIFF_MAX_LINE || b.length > CHAR_DIFF_MAX_LINE)
      continue;
    const { delRanges, addRanges, lcs } = lcsRanges(a, b);
    if (lcs / Math.max(a.length, b.length) < CHAR_DIFF_MIN_SIM) continue;
    wrapCharRanges(delCell, delRanges, "diff-char-del");
    wrapCharRanges(addCell, addRanges, "diff-char-add");
  }
};

const dispatchFile = (entry: PendingFile): void => {
  void diffPool.dispatch(entry.code, entry.lang).then((buf) => {
    if (buf) applyHighlight(entry.cells, buf);
    applyCharDiffToFile(entry.el);
  });
};

// Lazily install the pool's viewport observer with our intersection handler.
// The pool owns the observer; we own the pendingFiles map.
const ensureDiffObserver = (): IntersectionObserver =>
  diffPool.ensureObserver((entry) => {
    const target = entry.target as HTMLElement;
    const file = pendingFiles.get(target);
    if (file) {
      pendingFiles.delete(target);
      dispatchFile(file);
      diffPool.ensureObserver(() => {}).unobserve(target);
    }
  });

interface DiffHighlightHook extends LiveViewHook<never> {
  highlight(): void;
}

const DiffHighlight = {
  highlight(this: DiffHighlightHook) {
    const lang = this.el.dataset["lang"];
    if (!lang) return;
    diffPool.warmup(lang);
    const cells = Array.from(
      this.el.querySelectorAll<HTMLElement>(".diff-code"),
    );
    if (cells.length === 0 || cells[0]!.dataset["highlighted"]) return;

    const code = cells.map((c) => c.textContent).join("\n");
    const entry: PendingFile = { el: this.el, cells, code, lang };

    // Register with observer — fires immediately for visible files (async, no reflow)
    pendingFiles.set(this.el, entry);
    ensureDiffObserver().observe(this.el);
  },
  mounted(this: DiffHighlightHook) {
    this.highlight();
    if (!this.el.dataset["lang"]) applyCharDiffToFile(this.el);
    attachDiffInteractions(this.el, this);
  },
  updated(this: DiffHighlightHook) {
    this.highlight();
    if (!this.el.dataset["lang"]) applyCharDiffToFile(this.el);
    attachDiffInteractions(this.el, this);
  },
  destroyed(this: DiffHighlightHook) {
    detachDiffInteractions(this);
    // Best-effort unobserve via the pool. If the observer was never created
    // (e.g. hook torn down before first highlight), ensureObserver() will
    // create one to call unobserve on — harmless: it has zero observed nodes.
    diffPool.ensureObserver(() => {}).unobserve(this.el);
    pendingFiles.delete(this.el);
  },
};

// --- CodeRefAutocomplete: [[path]] autocomplete in textareas ---
// Pure helpers (matchAutocompleteTrigger, spliceAutocomplete) live in
// ./text-autocomplete and are re-exported at the top of this file.

const CodeRefAutocomplete = {
  mounted(this: LiveViewHook<SearchPushEventWire> & CodeRefAutocompleteState) {
    const el = this.el;
    const pushEvent = this.pushEvent.bind(this);
    this._dropdown = null;

    this._inputHandler = (e: Event) => {
      const ta = e.target as HTMLTextAreaElement | null;
      if (!ta || ta.tagName !== "TEXTAREA") return;
      const prefix = matchAutocompleteTrigger(
        ta.value.substring(0, ta.selectionStart),
      );
      if (prefix !== null) {
        pushEvent("autocompleteFiles", { prefix });
      } else if (this._dropdown) {
        this._dropdown.remove();
        this._dropdown = null;
      }
    };
    el.addEventListener("input", this._inputHandler);

    this.handleEvent("fileSuggestions", (payload: unknown) => {
      const { files } = payload as { files: string[] };
      if (this._dropdown) this._dropdown.remove();
      const ta = el.querySelector("textarea");
      if (!ta) return;
      this._dropdown = document.createElement("div");
      this._dropdown.className =
        "absolute z-20 bg-base border border-border rounded shadow-lg max-h-40 overflow-y-auto text-sm";
      this._dropdown.style.bottom = "100%";
      this._dropdown.style.left = "0";
      this._dropdown.style.marginBottom = "4px";
      const dd = this._dropdown;
      files.forEach((f: string) => {
        const opt = document.createElement("button");
        opt.type = "button";
        opt.className =
          "block w-full text-left px-3 py-1 hover:bg-surface text-text truncate";
        opt.textContent = f;
        opt.addEventListener("mousedown", (ev: Event) => {
          ev.preventDefault();
          const result = spliceAutocomplete(ta.value, ta.selectionStart, f);
          if (result) {
            ta.value = result.value;
            ta.selectionStart = ta.selectionEnd = result.cursor;
          }
          dd.remove();
          this._dropdown = null;
          ta.focus();
        });
        dd.appendChild(opt);
      });
      if (files.length > 0) {
        el.style.position = "relative";
        el.appendChild(dd);
      }
    });
  },
  destroyed(
    this: LiveViewHook<SearchPushEventWire> & CodeRefAutocompleteState,
  ) {
    if (this._inputHandler) {
      this.el.removeEventListener("input", this._inputHandler);
    }
    if (this._dropdown) {
      this._dropdown.remove();
      this._dropdown = null;
    }
  },
};

// --- MarkdownEditor: EasyMDE inline markdown editor ---

// Exported so `hooks-pure.test.ts` can pin the textarea-mirror contract that
// `code-browser.ts`s `liveBuffer()` reads through. Registered in the Hooks
// map below like every other hook.
export const MarkdownEditor = {
  mounted(this: LiveViewHook<EditorPushEventWire> & MarkdownEditorState) {
    const ta = this.el as HTMLTextAreaElement;

    const editor = new EasyMDE({
      element: ta,
      spellChecker: false,
      status: false,
      toolbar: [
        "bold",
        "italic",
        "heading",
        "|",
        "code",
        "quote",
        "unordered-list",
        "ordered-list",
        "|",
        "link",
        "image",
        "|",
        "preview",
        "guide",
      ],
      initialValue: ta.value || "",
      autoDownloadFontAwesome: true,
    });
    // Debounce the synthetic input event so a fast typist doesn't flood the
    // LiveView with one form-validate roundtrip per keystroke. CodeMirror's
    // `change` fires per keystroke; we coalesce to the trailing edge.
    // Closure-local timer sidesteps the LiveViewHook index-signature
    // collision that surfaces when destroyed() reads back through `this`.
    const DEBOUNCE_MS = 300;
    let timer: number | null = null;
    const flushDispatch = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
      ta.value = editor.value();
      ta.dispatchEvent(new Event("input", { bubbles: true }));
    };
    this["_flushDispatch"] = flushDispatch;
    this["_debounceTimer"] = null;
    // CONTRACT: the textarea mirrors the editor on every change, eagerly and
    // outside the debounce. EasyMDE owns the real buffer and only this hook
    // holds the instance, so `ta.value` is how anything else reads the live
    // text — the code browser's `liveBuffer()` does exactly that to carry
    // unsaved edits across an editor-mode switch. Moving this line inside
    // `flushDispatch` would leave readers up to 300ms stale with nothing
    // failing, so `hooks-markdown-editor.test.ts` pins it.
    editor.codemirror.on("change", () => {
      ta.value = editor.value();
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(flushDispatch, DEBOUNCE_MS);
    });
    // On submit, flush any pending dispatch so the form carries fresh text.
    const form = ta.closest("form");
    if (form) {
      this["_form"] = form;
      const submitHandler = (): void => {
        flushDispatch();
      };
      this["_formSubmitHandler"] = submitHandler;
      form.addEventListener("submit", submitHandler);
    }
    this._editor = editor;
    this.handleEvent("clearEditor", (payload: unknown) => {
      const { id } = payload as { id: string };
      if (id === ta.id && this._editor) {
        this._editor.value("");
        ta.value = "";
      }
    });

    // CodeMirror measures container width at init. When mounted inside a
    // modal panel that's still mid-GSAP open animation (opacity:0 +
    // transform), it gets a 0-width box and never recovers. The modal
    // dispatches a bubbling `modal:opened` event when its open animation
    // completes — refresh CodeMirror's geometry then.
    this._modalOpenedHandler = (e: Event) => {
      const target = e.target as Node | null;
      if (target && target.contains(ta) && this._editor) {
        this._editor.codemirror.refresh();
      }
    };
    document.addEventListener("modal:opened", this._modalOpenedHandler);
  },
  // No `updated()`. EasyMDE owns the editor content; trying to sync the
  // hidden textarea's value back into EasyMDE on every LiveView patch
  // calls `editor.value(...)` which yanks focus on every keystroke even
  // when the wrapper is `phx-update="ignore"` (Phoenix LV still fires
  // the hook's `updated()` callback). The `clear_editor` push event
  // remains as the explicit reset path.
  destroyed(this: LiveViewHook<EditorPushEventWire> & MarkdownEditorState) {
    const form = this["_form"];
    const submitHandler = this["_formSubmitHandler"];
    if (form && submitHandler) {
      form.removeEventListener("submit", submitHandler);
      this["_form"] = null;
      this["_formSubmitHandler"] = null;
    }
    this["_flushDispatch"] = null;
    this["_debounceTimer"] = null;
    if (this._modalOpenedHandler) {
      document.removeEventListener("modal:opened", this._modalOpenedHandler);
      this._modalOpenedHandler = null;
    }
    if (this._editor) {
      this._editor.toTextArea();
      this._editor = null;
    }
  },
};

// --- RecentCommits: stagger-animate newly loaded commit rows ---
// Tracks child count of [data-commits-list]; on update, GSAP-animates
// just the rows added since last render.

const RecentCommits = {
  mounted(this: LiveViewHook<never> & RecentCommitsState) {
    const list = this.el.querySelector<HTMLElement>("[data-commits-list]");
    this._lastCount = list ? list.children.length : 0;
    // First update() after mount isn't a "See more" — it's LiveView settling
    // its initial patch. Don't auto-scroll on that.
    this._skipNextUpdate = true;
  },
  updated(this: LiveViewHook<never> & RecentCommitsState) {
    const list = this.el.querySelector<HTMLElement>("[data-commits-list]");
    if (!list) return;
    const last = this._lastCount;
    const current = list.children.length;
    if (this._skipNextUpdate) {
      this._skipNextUpdate = false;
      this._lastCount = current;
      return;
    }
    if (current > last) {
      const newRows = Array.from(list.children).slice(last) as HTMLElement[];
      const firstNew = newRows[0];
      if (firstNew) {
        // Scroll the panel so the first new commit lands just below the
        // header — keeps the staggered fade-in within the viewport instead
        // of below the "See more" button.
        const containerRect = this.el.getBoundingClientRect();
        const firstNewRect = firstNew.getBoundingClientRect();
        const offset = firstNewRect.top - containerRect.top + this.el.scrollTop;
        gsap.to(this.el, {
          scrollTop: offset,
          duration: 0.5,
          ease: "power2.inOut",
        });
      }
      gsap.from(newRows, {
        opacity: 0,
        y: 12,
        duration: 0.4,
        stagger: 0.06,
        ease: "power2.out",
      });
    }
    this._lastCount = current;
  },
};

// Dropdown — extracted to ./dropdown for testability. Re-exported below.

// --- Toast auto-dismiss ---

const Toast = {
  mounted(this: LiveViewHook<never> & ToastState) {
    const el = this.el;
    this._timer = setTimeout(() => {
      el.classList.add(FADE_OUT);
      setTimeout(function () {
        el.remove();
      }, 200);
    }, 5000);
  },
  destroyed(this: LiveViewHook<never> & ToastState) {
    if (this._timer) clearTimeout(this._timer);
  },
};

// --- SshKeyPaste: drag-drop .pub files onto SSH key textarea ---

const SshKeyPaste = {
  mounted(this: LiveViewHook<never> & SshKeyPasteState) {
    const el = this.el;
    const textarea = el.querySelector("textarea");
    if (!textarea) {
      this._dragoverHandler = null;
      this._dragleaveHandler = null;
      this._dropHandler = null;
      return;
    }

    this._dragoverHandler = (e: Event) => {
      e.preventDefault();
      el.classList.add("ring-2", "ring-accent");
    };
    this._dragleaveHandler = () => {
      el.classList.remove("ring-2", "ring-accent");
    };
    this._dropHandler = (e: DragEvent) => {
      e.preventDefault();
      el.classList.remove("ring-2", "ring-accent");
      const files = e.dataTransfer?.files;
      const file = files ? files[0] : undefined;
      if (file && file.size <= 65536) {
        const reader = new FileReader();
        reader.onload = function (ev: ProgressEvent<FileReader>) {
          const result = ev.target?.result;
          if (
            typeof result === "string" &&
            /^(ssh-|ecdsa-|sk-|-----BEGIN)/.test(result.trim())
          ) {
            textarea.value = result;
            textarea.dispatchEvent(new Event("input", { bubbles: true }));
          }
        };
        reader.readAsText(file);
      }
    };

    el.addEventListener("dragover", this._dragoverHandler);
    el.addEventListener("dragleave", this._dragleaveHandler);
    el.addEventListener("drop", this._dropHandler);
  },
  destroyed(this: LiveViewHook<never> & SshKeyPasteState) {
    if (this._dragoverHandler)
      this.el.removeEventListener("dragover", this._dragoverHandler);
    if (this._dragleaveHandler)
      this.el.removeEventListener("dragleave", this._dragleaveHandler);
    if (this._dropHandler)
      this.el.removeEventListener("drop", this._dropHandler);
  },
};

// --- Clipboard: copy data-clipboard-text to clipboard on click ---

const Clipboard = {
  mounted(this: LiveViewHook<never> & ClipboardState) {
    const el = this.el;
    this._clickHandler = () => {
      const text = el.dataset["clipboardText"];
      if (text) {
        void navigator.clipboard
          .writeText(text)
          .then(function () {
            const original = el.textContent;
            el.textContent = "Copied!";
            setTimeout(function () {
              el.textContent = original;
            }, 1500);
          })
          .catch(function () {
            const original = el.textContent;
            el.textContent = "Failed";
            setTimeout(function () {
              el.textContent = original;
            }, 1500);
          });
      }
    };
    el.addEventListener("click", this._clickHandler);
  },
  destroyed(this: LiveViewHook<never> & ClipboardState) {
    if (this._clickHandler)
      this.el.removeEventListener("click", this._clickHandler);
  },
};

// --- PrTimeline: keyboard navigation for PR timeline ---

function focusTimelineEntry(
  entries: NodeListOf<HTMLElement>,
  idx: number,
): void {
  for (let i = 0; i < entries.length; i++) {
    entries[i]!.classList.toggle("ring-1", i === idx);
    entries[i]!.classList.toggle("ring-accent/30", i === idx);
  }
  const target = entries[idx];
  if (target) target.scrollIntoView({ block: "nearest", behavior: "smooth" });
}

const PrTimeline = {
  mounted(
    this: LiveViewHook<PrPushEventWire | EditorPushEventWire> & PrTimelineState,
  ) {
    const el = this.el;
    const pushEvent = this.pushEvent.bind(this);
    this._focused = -1;
    this._lineObserver = null;

    // After the CSS stagger has run for this page-load, mark <html> so the
    // .timeline-bootstrapped guard in app.css suppresses replays on
    // subsequent LiveView patches (Edit modal toggle, comment edit, etc.).
    // <html> is never re-created by morphdom so the marker survives.
    if (!document.documentElement.classList.contains("timeline-bootstrapped")) {
      const lastEntry = el.querySelector<HTMLElement>(
        "[data-timeline-entry]:last-of-type",
      );
      const markBootstrapped = () => {
        document.documentElement.classList.add("timeline-bootstrapped");
      };
      if (lastEntry) {
        lastEntry.addEventListener("animationend", markBootstrapped, {
          once: true,
        });
        // Belt + braces: if the element is recreated mid-animation and
        // animationend never fires, fall back on a deadline tied to the
        // longest expected stagger (entries * 60ms + 450ms duration + buffer).
        const entryCount = el.querySelectorAll("[data-timeline-entry]").length;
        const deadline = entryCount * 60 + 450 + 200;
        window.setTimeout(markBootstrapped, deadline);
      } else {
        markBootstrapped();
      }
    }

    // Timeline entry stagger-fade is now CSS-driven (see `.timeline-entry`
    // keyframes in app.css). Doing it in CSS means the animation kicks
    // off the moment the browser paints the markup — no waiting on the
    // 2.8 MB JS bundle to parse. JS used to GSAP this; the lag on a
    // fresh reload was unacceptable.

    // Position the dashed vertical line so it spans first→last icon center,
    // not the entire conversation container height. Server renders the line
    // with inline `opacity:0` to suppress FOUC; we flip it back to 0.1 once
    // top/height are computed. First call animates the height from 0 →
    // full so the line draws downwards on initial load; subsequent calls
    // (ResizeObserver) snap to the new size to avoid janky re-animation
    // every time a comment edit changes the layout.
    // Use the dox-generated `prTimelineLine` id rather than a JS-only
    // marker class — keeps the selector stable across Elixir, TS, CSS,
    // and tests (single source of truth in ui/pr.dox → PrElementId).
    const line = document.getElementById(validPrElementId("prTimelineLine"));
    const conversation = el.querySelector<HTMLElement>("#pr-conversation");
    this._lineDrawn = false;
    const positionLine = (): void => {
      if (!line || !conversation) return;
      const cells = conversation.querySelectorAll<HTMLElement>(
        "[data-timeline-icon]",
      );
      if (cells.length < 2) {
        // Don't fight the CSS gate (`:not(.timeline-line-positioned)
        // { opacity: 0 }`). Setting an inline `opacity:0` would also
        // get stomped by morphdom on the next patch — the class is
        // morphdom-stable and is the only flag that matters.
        line.classList.remove("timeline-line-positioned");
        return;
      }
      // The icon CELL stretches to the timeline-entry's full height (flex
      // row with default align-items: stretch). Measure the inner glyph
      // instead — that's what the user perceives as "the icon".
      const firstCell = cells[0]!;
      const lastCell = cells[cells.length - 1]!;
      const firstGlyph =
        (firstCell.firstElementChild as HTMLElement | null) ?? firstCell;
      const lastGlyph =
        (lastCell.firstElementChild as HTMLElement | null) ?? lastCell;

      const containerRect = conversation.getBoundingClientRect();
      const firstRect = firstGlyph.getBoundingClientRect();
      const lastRect = lastGlyph.getBoundingClientRect();
      const firstCenter =
        firstRect.top - containerRect.top + firstRect.height / 2;
      const lastCenter = lastRect.top - containerRect.top + lastRect.height / 2;
      const fullHeight = lastCenter - firstCenter;

      line.style.top = String(firstCenter) + "px";
      // Reveal via class (survives morphdom patches), not inline opacity.
      line.classList.add("timeline-line-positioned");

      if (this._lineDrawn) {
        line.style.height = String(fullHeight) + "px";
        return;
      }

      // First draw: ease the height from 0 to fullHeight so the dashed
      // line animates downwards. Pace matches the entry stagger
      // (entries × 60 ms) — the line completes drawing at the same time
      // as the last entry finishes fading in.
      this._lineDrawn = true;
      const entryCount = el.querySelectorAll("[data-timeline-entry]").length;
      const duration = Math.max(0.4, Math.min(1.2, entryCount * 0.06));
      gsap.fromTo(
        line,
        { height: 0 },
        { height: fullHeight, duration, ease: "power2.out" },
      );
    };

    positionLine();

    if (conversation && typeof ResizeObserver !== "undefined") {
      this._lineObserver = new ResizeObserver(() => {
        positionLine();
      });
      this._lineObserver.observe(conversation);
    }

    this._keyHandler = (e: KeyboardEvent) => {
      // Uses shared isEditableTarget — also guards against CM focus + <select>,
      // an intentional improvement over the original tag-only check.
      if (isEditableTarget(e)) return;

      const entries = el.querySelectorAll<HTMLElement>("[data-timeline-entry]");
      if (!entries.length) return;

      switch (e.key) {
        case "j":
          e.preventDefault();
          this._focused = Math.min(this._focused + 1, entries.length - 1);
          focusTimelineEntry(entries, this._focused);
          break;
        case "k":
          e.preventDefault();
          this._focused = Math.max(this._focused - 1, 0);
          focusTimelineEntry(entries, this._focused);
          break;
        case "n":
          e.preventDefault();
          pushEvent("focusNextUnresolved", {});
          break;
        case "r":
          e.preventDefault();
          if (this._focused >= 0 && entries[this._focused]) {
            const cid =
              entries[this._focused]!.querySelector<HTMLElement>(
                "[data-comment-id]",
              );
            if (cid)
              pushEvent("startReply", {
                comment_id: cid.dataset["commentId"]!,
              });
          }
          break;
        case "e":
          e.preventDefault();
          if (this._focused >= 0 && entries[this._focused]) {
            const cidEl =
              entries[this._focused]!.querySelector<HTMLElement>(
                "[data-comment-id]",
              );
            if (cidEl)
              pushEvent("startEditComment", {
                id: cidEl.dataset["commentId"]!,
              });
          }
          break;
      }
    };

    document.addEventListener("keydown", this._keyHandler);

    this.handleEvent("scrollTo", function (payload: unknown) {
      const { id } = payload as { id: string };
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ block: "center", behavior: "smooth" });
        target.classList.add("ring-1", "ring-accent/30");
        setTimeout(function () {
          target.classList.remove("ring-1", "ring-accent/30");
        }, 2000);
      }
    });
  },
  destroyed(
    this: LiveViewHook<PrPushEventWire | EditorPushEventWire> & PrTimelineState,
  ) {
    if (this._keyHandler)
      document.removeEventListener("keydown", this._keyHandler);
    if (this._lineObserver) {
      this._lineObserver.disconnect();
      this._lineObserver = null;
    }
  },
};

// --- CommitSelect: shift/ctrl multi-select for PR commit panel ---
// Intercepts clicks on .commit-select-btn elements and computes the
// new selection based on modifier keys, then pushes "selectCommits"
// to the server with the resulting hash list.

interface CommitSelectState {
  _clickHandler: ((e: MouseEvent) => void) | null;
}

const CommitSelect = {
  mounted(this: LiveViewHook<EditorPushEventWire> & CommitSelectState) {
    const el = this.el;
    const pushEvent = this.pushEvent.bind(this);

    this._clickHandler = (e: MouseEvent): void => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>(
        ".commit-select-btn",
      );
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const hash = btn.dataset["commitHash"];
      if (!hash) return;

      const allHashes = JSON.parse(el.dataset["allHashes"] ?? "[]") as string[];
      const selected = JSON.parse(el.dataset["selected"] ?? "[]") as string[];
      const lastClicked = el.dataset["lastClicked"] ?? "";

      let next: string[];

      if (e.shiftKey && lastClicked) {
        // Shift+click: range from lastClicked to hash
        const fromIdx = allHashes.indexOf(lastClicked);
        const toIdx = allHashes.indexOf(hash);
        if (fromIdx >= 0 && toIdx >= 0) {
          const lo = Math.min(fromIdx, toIdx);
          const hi = Math.max(fromIdx, toIdx);
          const range = allHashes.slice(lo, hi + 1);
          // Union with existing selection
          const set = new Set(selected);
          for (const h of range) set.add(h);
          next = allHashes.filter((h) => set.has(h));
        } else {
          next = [hash];
        }
      } else if (e.ctrlKey || e.metaKey || e.altKey) {
        // Ctrl/Cmd/Alt+click: toggle individual
        const set = new Set(selected);
        if (set.has(hash)) {
          set.delete(hash);
        } else {
          set.add(hash);
        }
        // Preserve chronological order
        next = allHashes.filter((h) => set.has(h));
      } else {
        // Plain click: single select
        next = [hash];
      }

      pushEvent("selectCommits", {
        hashes: next,
        last_clicked: hash,
      });
    };

    el.addEventListener("click", this._clickHandler);
  },

  destroyed(this: LiveViewHook<EditorPushEventWire> & CommitSelectState) {
    if (this._clickHandler) {
      this.el.removeEventListener("click", this._clickHandler);
    }
  },
};

interface FoldBoundElement extends HTMLDetailsElement {
  _foldBound?: boolean;
  // True iff the user has explicitly collapsed this <details>. We re-apply
  // the closed state after every LiveView patch because morphdom keeps
  // re-adding the `open` attribute that HEEx always renders — without this
  // flag, any unrelated LV diff (a comment, a label change, an inline
  // comment posted to a different file) silently re-opens collapsed ones.
  _userClosed?: boolean;
}

// --- DetailsFold: 3D fold animation for <details> toggle ---
// Attach to a container; all <details> children get animated open/close.
// Content folds on the X-axis with perspective, matching the sidebar 3D style.

const FOLD_DURATION = 0.3;

const animateOpen = (content: HTMLElement): void => {
  gsap.killTweensOf(content);
  content.style.overflow = "hidden";
  const h = content.scrollHeight;
  // Start from natural state (rotationX:0) — the bend peaks mid-animation
  // via a custom onUpdate curve, not via start/end values that would snap.
  const proxy = { t: 0 };
  gsap.fromTo(
    proxy,
    { t: 0 },
    {
      t: 1,
      duration: FOLD_DURATION,
      ease: "power2.inOut",
      onUpdate: () => {
        const t = proxy.t;
        // Height: linear 0→h
        content.style.height = String(t * h) + "px";
        // Opacity: 0.5→1
        content.style.opacity = String(0.5 + t * 0.5);
        // Bend: peaks at t=0.5, zero at endpoints — smooth parabola
        const bend = -20 * t * (1 - t);
        content.style.transform =
          "perspective(2000px) rotateX(" + String(bend) + "deg)";
        content.style.transformOrigin = "top center";
      },
      onComplete: () => {
        content.removeAttribute("style");
      },
    },
  );
};

const animateClose = (
  details: HTMLDetailsElement,
  content: HTMLElement,
): void => {
  gsap.killTweensOf(content);
  const h = content.offsetHeight;
  content.style.overflow = "hidden";
  const proxy = { t: 0 };
  gsap.fromTo(
    proxy,
    { t: 0 },
    {
      t: 1,
      duration: FOLD_DURATION,
      ease: "power2.inOut",
      onUpdate: () => {
        const t = proxy.t;
        // Height: h→0
        content.style.height = String((1 - t) * h) + "px";
        // Opacity: 1→0.5
        content.style.opacity = String(1 - t * 0.5);
        // Bend: same parabola, peaks mid-animation
        const bend = -20 * t * (1 - t);
        content.style.transform =
          "perspective(2000px) rotateX(" + String(bend) + "deg)";
        content.style.transformOrigin = "top center";
      },
      onComplete: () => {
        details.removeAttribute("open");
        (details as FoldBoundElement)._userClosed = true;
        content.removeAttribute("style");
      },
    },
  );
};

const bindDetails = (details: HTMLDetailsElement): void => {
  const summary = details.querySelector("summary");
  if (!summary || (details as FoldBoundElement)._foldBound) return;
  (details as FoldBoundElement)._foldBound = true;

  summary.addEventListener("click", (e: MouseEvent) => {
    e.preventDefault();
    // Find the content element (first child after summary)
    const content = Array.from(details.children).find(
      (c) => c.tagName !== "SUMMARY",
    ) as HTMLElement | null;
    if (!content) {
      details.toggleAttribute("open");
      return;
    }

    if (details.open) {
      // Closing
      animateClose(details, content);
    } else {
      // Opening — set open first so content is rendered, then animate in
      (details as FoldBoundElement)._userClosed = false;
      details.setAttribute("open", "");
      animateOpen(content);
    }
  });
};

export const DetailsFold = {
  mounted(this: LiveViewHook<never>) {
    this.el
      .querySelectorAll<HTMLDetailsElement>("details")
      .forEach(bindDetails);
  },
  updated(this: LiveViewHook<never>) {
    this.el.querySelectorAll<HTMLDetailsElement>("details").forEach((d) => {
      bindDetails(d);
      // morphdom re-renders `<details open>` from the HEEx source on every
      // patch, which silently re-opens any file the user collapsed. Honor
      // the JS-side _userClosed flag and strip `open` back off so the user
      // stays in control of which files are visible.
      if ((d as FoldBoundElement)._userClosed) {
        d.removeAttribute("open");
      }
    });
  },
};

// --- DiffLazyLoad: stream more diff files as user scrolls ---
// Sentinel element at the bottom of the rendered file list. When it enters
// the viewport (with 400px lookahead), push "load_more_diff_files" to the
// component so visible_file_count bumps by @page_size.
//
// An observer reports crossings only. When a page of short files still
// leaves the sentinel inside the lookahead band, no new crossing ever
// happens and the list stops growing. So once the server has applied a
// page (the push's reply callback runs after the patch), observe the
// sentinel afresh: a new observation always delivers one entry with the
// current state, which asks for the next page while the sentinel is
// still in view. The sentinel is removed when every file is visible.

interface DiffLazyLoadState {
  _observer: IntersectionObserver | null;
}

const DiffLazyLoad = {
  mounted(this: LiveViewHook & DiffLazyLoadState) {
    const rearm = (): void => {
      const observer = this._observer;
      if (observer === null) return;
      observer.unobserve(this.el);
      observer.observe(this.el);
    };
    this._observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        this.pushEventTo(this.el, "load_more_diff_files", {}, rearm);
      },
      { rootMargin: "400px" },
    );
    this._observer.observe(this.el);
  },
  destroyed(this: LiveViewHook & DiffLazyLoadState) {
    this._observer?.disconnect();
    this._observer = null;
  },
};

// PipelineFavicon — extracted to ./pipeline-favicon for testability and
// to keep this index trimmer. Re-exported below.

// --- Export all hooks keyed by generated hook-name constants ---
// Originally one `satisfies Record<PhxHook, object>` exhaustiveness
// check; now ten smaller per-domain unions. The intersection-of-records
// shape enforces exhaustiveness per union independently — tsc still
// catches any gap between a .dox union and its TS implementation.

/** @public Imported by app.ts — knip can't trace the @dox/* path alias in the nix sandbox */
export const Hooks = {
  // Editor cluster
  codeEditor: CodeEditor,
  commentAnchor: CommentAnchor,
  historyViewer: HistoryViewerHook,
  readmeHighlight: ReadmeHighlight,
  diffHighlight: DiffHighlight,
  codeRefAutocomplete: CodeRefAutocomplete,
  markdownEditor: MarkdownEditor,
  diffInlineWrap: DiffInlineWrap,
  diffCommentBtn: DiffCommentBtn,
  paneDivider: PaneDivider,
  vimToggle: VimToggle,
  editorModeButton: EditorModeButton,
  workspaceTerminal: WorkspaceTerminal,
  scrollPreserve: {
    beforeUpdate(this: LiveViewHook<never> & { _scrollTop: number }) {
      this._scrollTop = this.el.scrollTop;
    },
    updated(this: LiveViewHook<never> & { _scrollTop: number }) {
      this.el.scrollTop = this._scrollTop;
    },
  },

  // Cross-cutting UI chrome
  modal: Modal,
  toast: Toast,
  dropdown: Dropdown,
  clipboard: Clipboard,
  tableSortAnimate: TableSortAnimate,
  detailsFold: DetailsFold,
  keyboardShortcuts: KeyboardShortcuts,
  rowExpander: RowExpander,
  commitPanelToggle: CommitPanelToggle,
  fileExpander: FileExpander,
  deleteConfirmGate: DeleteConfirmGate,
  workspacesNavToggle: WorkspacesNavToggle,
  clientTableSort: ClientTableSort,
  clientListFilter: ClientListFilter,
  disclosureToggle: DisclosureToggle,
  sectionToggle: SectionToggle,
  profileDropdown: ProfileDropdown,

  // Omnisearch
  omniSearchTrigger: OmniSearchTrigger,
  omniSearch: OmniSearch,

  // Branch/tag/idea-lane picker
  branchSelect: BranchSelect,

  // Auth
  authError: AuthError,
  authCard: AuthCard,

  // SSH
  sshKeyPaste: SshKeyPaste,

  // PR
  prTimeline: PrTimeline,
  inlineCommentForm: InlineCommentForm,
  diffLazyLoad: DiffLazyLoad,

  // Theme
  themeApply: ThemeApply,

  // Profile
  obsidianBg: DotMatrixBg,
  contribTerrain: ContribTerrain,
  profileIdentity: ProfileIdentity,
  profileAbout: ProfileAbout,
  projectStories: ProjectStories,
  activityViz: ActivityViz,
  engagement: Engagement,

  // CI / pipeline visualisations
  recentCommits: RecentCommits,
  commitSelect: CommitSelect,
  dagGraph: DagGraph,
  buildLog: BuildLog,
  sparkline: Sparkline,
  ciAnimations: CiAnimations,
  kdeChart: KdeChart,
  scatterPlot: ScatterPlot,
  jobDetailPanel: JobDetailPanel,
  pipelineFavicon: PipelineFavicon,
  jobMetricsChart: JobMetricsChart,
  runnerMetricsChart: RunnerMetricsChart,
  sessionMetricsChart: SessionMetricsChart,
  // LiveDuration is rendered as a <live-duration> custom element (registered
  // below) rather than a LiveView hook, but the CiHook union requires an
  // entry so the satisfies check stays exhaustive.
  liveDuration: {},
  // EvalLog streams nix eval stderr to a transient `<eval-log>` element
  // on the pipeline page (visible only during the evaluating phase).
  // Added to ui/ci.dox CiHook so the per-domain exhaustiveness stays
  // honest — the LiveView template uses `OperationWeb.Hooks.eval_log()`
  // as the phx-hook attribute value.
  evalLog: EvalLog,
} satisfies Record<
  | AuthHook
  | CiHook
  | EditorHook
  | OmnisearchHook
  | PrHook
  | ProfileHook
  | SshHook
  | ThemeHook
  | UiHook,
  object
>;

// --- <live-duration> custom element: ticks elapsed time every second ---
// Server renders <live-duration data-started-at="<ms_epoch>"></live-duration>
// and the element manages its own interval — no PhxHook required.
class LiveDurationElement extends HTMLElement {
  private _timer: ReturnType<typeof setInterval> | undefined;

  connectedCallback() {
    this.render();
    this._timer = setInterval(() => {
      this.render();
    }, 1000);
  }

  disconnectedCallback() {
    if (this._timer !== undefined) {
      clearInterval(this._timer);
      this._timer = undefined;
    }
  }

  static get observedAttributes() {
    return ["data-started-at"];
  }

  attributeChangedCallback() {
    this.render();
  }

  private render() {
    const started = Number(this.dataset["startedAt"]);
    if (!Number.isFinite(started) || started <= 0) {
      this.textContent = "—";
      return;
    }
    this.textContent = formatDurationMs(Date.now() - started);
  }
}

if (!customElements.get("live-duration")) {
  customElements.define("live-duration", LiveDurationElement);
}
